import React, { useState, useEffect, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TextField, Box, Typography, FormControl, Select, MenuItem } from "@mui/material";
import { useTheme } from "@emotion/react";
import { tokens } from "../theme";

const DateOfBirthPicker = memo(({
  value,
  onChange,
  placeholder = "Select date of birth",
  label,
  error,
  helperText,
  required = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('addPatient');

  // Parse current value
  const parseDate = (dateString) => {
    if (!dateString) return { year: "", month: "", day: "" };
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return { year: "", month: "", day: "" };
      return {
        year: date.getFullYear().toString(),
        month: String(date.getMonth() + 1).padStart(2, '0'),
        day: String(date.getDate()).padStart(2, '0'),
      };
    } catch {
      return { year: "", month: "", day: "" };
    }
  };

  const [dateParts, setDateParts] = useState(parseDate(value));
  const [isInternalChange, setIsInternalChange] = useState(false);

  // Update when value prop changes (only if change is external, not from our own onChange)
  useEffect(() => {
    if (!isInternalChange) {
      setDateParts(parseDate(value));
    }
    setIsInternalChange(false);
  }, [value]);

  // Get days in month - improved
  const getDaysInMonth = (year, month) => {
    if (!year || !month || year === "" || month === "") return 31;
    const yearNum = parseInt(year);
    const monthNum = parseInt(month);
    if (isNaN(yearNum) || isNaN(monthNum) || monthNum < 1 || monthNum > 12) return 31;
    // Use the actual month (monthNum) to get days in that month
    return new Date(yearNum, monthNum, 0).getDate();
  };

  // Calculate days in month based on current year and month
  const daysInMonth = useMemo(() => {
    return getDaysInMonth(dateParts.year, dateParts.month);
  }, [dateParts.year, dateParts.month]);

  const days = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  }, [daysInMonth]);

  // Validate and format date
  const handleDateChange = (newYear, newMonth, newDay, skipValidation = false) => {
    // Update state immediately - preserve existing values if new value is undefined
    const updatedParts = {
      year: newYear !== undefined ? newYear : dateParts.year,
      month: newMonth !== undefined ? newMonth : dateParts.month,
      day: newDay !== undefined ? newDay : dateParts.day,
    };

    setDateParts(updatedParts);

    // If skipValidation is true, don't call onChange (for intermediate typing)
    if (skipValidation) {
      return;
    }

    const { year, month, day } = updatedParts;

    // Only call onChange if all parts are filled and valid
    if (year && month && day) {
      const yearNum = parseInt(year);
      const monthNum = parseInt(month);
      const dayNum = parseInt(day);

      // Validate all parts are numbers
      if (!isNaN(yearNum) && !isNaN(monthNum) && !isNaN(dayNum)) {
        // Validate year range
        const currentYear = new Date().getFullYear();
        const minYear = currentYear - 150;
        const maxYear = currentYear;

        if (yearNum >= minYear && yearNum <= maxYear && monthNum >= 1 && monthNum <= 12) {
          // Get max day for the month/year
          const maxDay = getDaysInMonth(year, month);
          const validDay = Math.min(Math.max(1, dayNum), maxDay);
          
          // Create date object
          const date = new Date(yearNum, monthNum - 1, validDay);
          
          // Check if date is valid and not in the future
          if (!isNaN(date.getTime())) {
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            
            if (date <= today) {
              // Format as YYYY-MM-DD
              const formattedDate = `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(validDay).padStart(2, '0')}`;
              setIsInternalChange(true);
              onChange(formattedDate);
              return;
            }
          }
        }
      }
    }
    
    // If invalid or incomplete, only clear if we had a previous complete valid value
    // Don't clear if user is just typing or deleting one field
    // Only clear if all three fields were previously filled and now one is missing
    const hadCompleteDate = value && dateParts.year && dateParts.month && dateParts.day;
    const nowIncomplete = !year || !month || !day;
    
    if (hadCompleteDate && nowIncomplete) {
      setIsInternalChange(true);
      onChange("");
    }
  };

  const months = useMemo(() => [
    { value: "01", label: t('january') },
    { value: "02", label: t('february') },
    { value: "03", label: t('march') },
    { value: "04", label: t('april') },
    { value: "05", label: t('may') },
    { value: "06", label: t('june') },
    { value: "07", label: t('july') },
    { value: "08", label: t('august') },
    { value: "09", label: t('september') },
    { value: "10", label: t('october') },
    { value: "11", label: t('november') },
    { value: "12", label: t('december') },
  ], [t]);

  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 150;
  const maxYear = currentYear;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {label && (
        <Typography
          variant="caption"
          sx={{
            color: colors.grey[300],
            fontWeight: "500",
            fontSize: "0.875rem",
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
        }}
      >
        {/* Month Dropdown */}
        <FormControl
          variant="filled"
          sx={{
            flex: 1,
            minWidth: 120,
            "& .MuiFilledInput-root": {
              backgroundColor: colors.primary[600],
              borderRadius: 2,
              border: `2px solid ${
                error
                  ? `${colors.redAccent[500]}60`
                  : `${colors.blueAccent[500]}40`
              }`,
              transition: "all 0.3s",
              "&:hover": {
                backgroundColor: disabled ? colors.primary[600] : colors.primary[500],
                borderColor: error
                  ? colors.redAccent[400]
                  : colors.blueAccent[400],
              },
              "&.Mui-focused": {
                backgroundColor: colors.primary[500],
                borderColor: error
                  ? colors.redAccent[500]
                  : colors.blueAccent[500],
                boxShadow: `0 0 0 3px ${
                  error
                    ? `${colors.redAccent[500]}33`
                    : `${colors.blueAccent[500]}33`
                }`,
              },
            },
            "& .MuiSelect-select": {
              color: colors.grey[100],
              fontSize: "0.95rem",
              fontWeight: 500,
              padding: "14px",
            },
          }}
        >
          <Select
            value={dateParts.month || ""}
            onChange={(e) => {
              const newMonth = e.target.value;
              // If day is invalid for new month, adjust it
              const maxDay = getDaysInMonth(dateParts.year, newMonth);
              let newDay = dateParts.day;
              
              if (dateParts.day && parseInt(dateParts.day) > maxDay) {
                newDay = maxDay.toString().padStart(2, '0');
              }
              
              handleDateChange(dateParts.year, newMonth, newDay);
            }}
            disabled={disabled}
            displayEmpty
            sx={{
              "& .MuiSelect-icon": {
                color: colors.grey[300],
              },
            }}
          >
            <MenuItem value="" disabled>
              <em>{t('month')}</em>
            </MenuItem>
            {months.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Day Dropdown */}
        <FormControl
          variant="filled"
          sx={{
            flex: 1,
            minWidth: 80,
            "& .MuiFilledInput-root": {
              backgroundColor: colors.primary[600],
              borderRadius: 2,
              border: `2px solid ${
                error
                  ? `${colors.redAccent[500]}60`
                  : `${colors.blueAccent[500]}40`
              }`,
              transition: "all 0.3s",
              "&:hover": {
                backgroundColor: disabled ? colors.primary[600] : colors.primary[500],
                borderColor: error
                  ? colors.redAccent[400]
                  : colors.blueAccent[400],
              },
              "&.Mui-focused": {
                backgroundColor: colors.primary[500],
                borderColor: error
                  ? colors.redAccent[500]
                  : colors.blueAccent[500],
                boxShadow: `0 0 0 3px ${
                  error
                    ? `${colors.redAccent[500]}33`
                    : `${colors.blueAccent[500]}33`
                }`,
              },
            },
            "& .MuiSelect-select": {
              color: colors.grey[100],
              fontSize: "0.95rem",
              fontWeight: 500,
              padding: "14px",
            },
          }}
        >
          <Select
            value={dateParts.day || ""}
            onChange={(e) => {
              const dayValue = e.target.value;
              handleDateChange(dateParts.year, dateParts.month, dayValue);
            }}
            disabled={disabled || !dateParts.month}
            displayEmpty
            sx={{
              "& .MuiSelect-icon": {
                color: colors.grey[300],
              },
            }}
          >
            <MenuItem value="" disabled>
              <em>{t('day')}</em>
            </MenuItem>
            {days.map((day) => (
              <MenuItem key={day} value={String(day).padStart(2, '0')}>
                {day}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Year Input */}
        <TextField
          variant="filled"
          placeholder={t('year')}
          value={dateParts.year}
          onChange={(e) => {
            const newYear = e.target.value.replace(/\D/g, ''); // Only numbers
            // Allow typing up to 4 digits
            if (newYear === "" || newYear.length <= 4) {
              // Update state immediately for typing - preserve month and day
              setDateParts({
                year: newYear,
                month: dateParts.month,
                day: dateParts.day
              });
              
              // If day is invalid for new year/month, adjust it
              if (newYear && dateParts.month) {
                const maxDay = getDaysInMonth(newYear, dateParts.month);
                let newDay = dateParts.day;
                
                if (dateParts.day && parseInt(dateParts.day) > maxDay) {
                  newDay = maxDay.toString().padStart(2, '0');
                  setDateParts({
                    year: newYear,
                    month: dateParts.month,
                    day: newDay
                  });
                  // Validate the adjusted date
                  setTimeout(() => {
                    handleDateChange(newYear, dateParts.month, newDay);
                  }, 0);
                } else if (newYear.length === 4 && dateParts.month && dateParts.day) {
                  // If year is complete (4 digits), validate the full date
                  setTimeout(() => {
                    handleDateChange(newYear, dateParts.month, dateParts.day);
                  }, 0);
                }
              }
            }
          }}
          onBlur={(e) => {
            // Validate range when user leaves the field
            const yearNum = parseInt(dateParts.year);
            if (dateParts.year) {
              if (isNaN(yearNum) || yearNum < minYear || yearNum > maxYear) {
                // Only clear year, keep month and day
                setDateParts({
                  ...dateParts,
                  year: ""
                });
                // Clear the value if we had a complete date before
                if (value && dateParts.month && dateParts.day) {
                  onChange("");
                }
              } else {
                // Re-validate the complete date
                handleDateChange(dateParts.year, dateParts.month, dateParts.day);
              }
            } else {
              // Year is empty, just validate what we have
              if (dateParts.month && dateParts.day) {
                // We have month and day but no year, don't call onChange
                // Just keep the state as is
              }
            }
          }}
          disabled={disabled}
          inputProps={{
            maxLength: 4,
          }}
          sx={{
            flex: 1,
            minWidth: 100,
            "& .MuiFilledInput-root": {
              backgroundColor: colors.primary[600],
              borderRadius: 2,
              border: `2px solid ${
                error
                  ? `${colors.redAccent[500]}60`
                  : `${colors.blueAccent[500]}40`
              }`,
              transition: "all 0.3s",
              "&:hover": {
                backgroundColor: disabled ? colors.primary[600] : colors.primary[500],
                borderColor: error
                  ? colors.redAccent[400]
                  : colors.blueAccent[400],
              },
              "&.Mui-focused": {
                backgroundColor: colors.primary[500],
                borderColor: error
                  ? colors.redAccent[500]
                  : colors.blueAccent[500],
                boxShadow: `0 0 0 3px ${
                  error
                    ? `${colors.redAccent[500]}33`
                    : `${colors.blueAccent[500]}33`
                }`,
              },
            },
            "& .MuiFilledInput-input": {
              color: colors.grey[100],
              fontSize: "0.95rem",
              fontWeight: 500,
              padding: "14px",
              textAlign: "center",
            },
          }}
        />
      </Box>
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: colors.redAccent[400],
            fontSize: "0.75rem",
            marginTop: "-4px",
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
});

DateOfBirthPicker.displayName = "DateOfBirthPicker";

export default DateOfBirthPicker;
